import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FiLock } from 'react-icons/fi';
import { authService } from '../services/authService';

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [notification, setNotification] = useState(null);
  const [token, setToken] = useState('');
  const [tokenError, setTokenError] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract token from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const resetToken = params.get('token');
    
    if (!resetToken) {
      setTokenError(true);
    } else {
      setToken(resetToken);
    }
  }, [location]);
  
  // Hide header and footer on this page
  useEffect(() => {
    // Detect iOS for specific fixes
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    
    if (isIOS) {
      document.documentElement.classList.add('ios');
      document.body.classList.add('ios');
    }
    
    // Add classes to hide header/footer and allow scrolling
    document.documentElement.classList.add('allow-scroll');
    document.body.classList.add('allow-scroll');
    
    return () => {
      // Cleanup on unmount
      if (isIOS) {
        document.documentElement.classList.remove('ios');
        document.body.classList.remove('ios');
      }
      document.documentElement.classList.remove('allow-scroll');
      document.body.classList.remove('allow-scroll');
    };
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    
    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (errors.password) {
      setErrors({...errors, password: null});
    }
  };

  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    if (errors.confirmPassword) {
      setErrors({...errors, confirmPassword: null});
    }
  };

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsLoading(true);
      
      try {
        await authService.resetPassword(token, password);
        
        // Show success state
        setIsSubmitted(true);
        showNotification('Your password has been reset successfully');
      } catch (error) {
        let errorMessage = 'Failed to reset password. Please try again.';
        
        if (error.response) {
          if (error.response.status === 400) {
            errorMessage = error.response.data.error || 'Invalid request';
          } else if (error.response.status === 410) {
            errorMessage = 'Reset link has expired. Please request a new one.';
          }
        }
        
        showNotification(errorMessage, 'error');
        console.error('Password reset error:', error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // If token is missing, show error state
  if (tokenError) {
    return (
      <div className="auth-page-container" style={{ background: 'linear-gradient(180deg, #E6DFED 0%, #E7E8DD 50%, #F2DAE2 100%)' }}>
        <div className="mx-auto px-4 py-10 relative z-10">
          <div className="w-full max-w-md mx-auto">
            <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
              <div className="text-center space-y-4">
                <div className="rounded-full bg-red-100 p-3 w-16 h-16 flex items-center justify-center mx-auto">
                  <svg
                    viewBox="0 0 24 24"
                    width="32"
                    height="32"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-red-600"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                </div>
                <h2 className="text-xl font-medium mt-4">Invalid Reset Link</h2>
                <p className="text-gray-500">
                  The password reset link is invalid or has expired.<br />
                  Please request a new password reset link.
                </p>
                <div className="mt-4">
                  <Link
                    to="/forgot-password"
                    className="mt-4 w-full auth-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white inline-block py-2 px-4"
                  >
                    Request New Link
                  </Link>
                </div>
                <div className="mt-4">
                  <Link
                    to="/signin"
                    className="text-purple-600 font-medium hover:text-purple-500"
                  >
                    Return to sign in
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page-container" style={{ background: 'linear-gradient(180deg, #E6DFED 0%, #E7E8DD 50%, #F2DAE2 100%)' }}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 p-4 rounded shadow-lg z-50 ${
          notification.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
        }`}>
          {notification.message}
        </div>
      )}
      
      {/* Background decorative elements - fixed position */}
      <div className="fixed top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[10%] left-[15%] w-64 h-64 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob"></div>
        <div className="absolute top-[40%] right-[15%] w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-2000"></div>
        <div className="absolute bottom-[10%] left-[35%] w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-blob animation-delay-4000"></div>
      </div>
      
      {/* Content container */}
      <div className="mx-auto px-4 py-10 relative z-10">
        <div className="w-full max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 space-y-6">
            {isSubmitted ? (
              <div className="text-center space-y-4">
                <div className="rounded-full bg-green-100 p-3 w-16 h-16 flex items-center justify-center mx-auto">
                  <svg
                    viewBox="0 0 24 24"
                    width="32"
                    height="32"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-green-600"
                  >
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                </div>
                <h2 className="text-xl font-medium mt-4">Password Reset Complete</h2>
                <p className="text-gray-500">
                  Your password has been reset successfully.<br />
                  You can now sign in with your new password.
                </p>
                <div className="mt-4">
                  <Link
                    to="/signin"
                    className="mt-4 w-full auth-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white inline-block py-2 px-4"
                  >
                    Sign In
                  </Link>
                </div>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                    Reset Password
                  </h1>
                  <p className="text-gray-500 mt-2">
                    Enter your new password below
                  </p>
                </div>
                
                <div className="space-y-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {errors.form && (
                      <div className="p-3 bg-red-50 text-red-700 text-sm rounded">
                        {errors.form}
                      </div>
                    )}
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">New Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiLock className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="password"
                          name="password"
                          placeholder="••••••••"
                          className={`auth-input ${errors.password ? 'border-red-300' : ''}`}
                          value={password}
                          onChange={handlePasswordChange}
                        />
                      </div>
                      {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
                    </div>
                    
                    <div className="space-y-1">
                      <label className="block text-sm font-medium text-gray-700">Confirm Password</label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <FiLock className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="password"
                          name="confirmPassword"
                          placeholder="••••••••"
                          className={`auth-input ${errors.confirmPassword ? 'border-red-300' : ''}`}
                          value={confirmPassword}
                          onChange={handleConfirmPasswordChange}
                        />
                      </div>
                      {errors.confirmPassword && <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>}
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full auth-button bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white mt-4"
                      disabled={isLoading}
                    >
                      {isLoading ? "Updating..." : "Reset Password"}
                    </button>
                  </form>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage; 